/* eslint-disable eqeqeq */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SearchExistingVisitorInfoCard from "../../../components/card/SearchExistingVisitorInfoCard";
import VisitorDetailsCard from "../../../components/card/VisitorDetailsCard";
import VisitDetailsCard from "../../../components/card/VisitDetailsCard";
import AttachmentsCard from "../../../components/card/AttachmentsCard";
import { Button } from "../../../components/button";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fileToBase64,
  formattedDate,
  formattedDateTime,
  isVisitWithinVisitingHours,
  mapToOptions,
  onlyDate,
} from "../../../lib/functions";
import {
  AddAttachments,
  API_ERRORS,
  Device,
  DOC_TYPE_ID,
  DYNAMIC_WORKFLOW,
  INVALID_PHOTO_ERRORS,
  STATUS_IDS,
  USER_ROLES_IDS,
  UserAppointment,
  VIPData,
  VISIT_CREATED_FROM,
  VisitorType,
} from "../../../constants/common";
import {
  useAddAttachment,
  useAddVisitorAppointment,
  useApproveDevice,
  useGetDeviceDetailsById,
  useValidatePhoto,
} from "../../../hooks/visitor/mutation";
import useAuthStore from "../../../store/Auth";
import { APP_ROUTES } from "../../../routes";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../../../components/loader";
import { createVIPSchema } from "../../../schemas/VIPSchema";
import DeviceDetailsCard from "../../../components/card/DeviceDetailsCard";
import BackButton from "../../../components/button/BackButton";
import {
  useGetAllVisitPurposesDropdown,
  useGetDynamicWorkflowsByIdByNonAdmin,
  useGetValidatePhoto,
  useGetVisitingHours,
} from "../../../hooks/common/query";
import VehicleInformationCard from "../../../components/card/VehicleInformationCard";
import CheckboxInput from "../../../components/inputs/CheckboxInput";

const VIP = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  const [isHostAccompanyPledge, setIsHostAccompanyPledge] = useState(false);

  const [existingVisitorAppointment, setExistingVisitorAppointment] =
    useState<any>();

  const [userAppointmentDetails, setUserAppointmentDetails] =
    useState<UserAppointment>();

  const [isDeviceDetails, setIsDeviceDetails] = useState(true);

  const { t } = useTranslation();

  const { data: allVisitPurposes } = useGetAllVisitPurposesDropdown({
    encryptedVisitRequestTypeID: "",
    encryptedId: "",
  });

  const visitPurposes = useMemo(
    () =>
      mapToOptions(
        "s_PurposeEn",
        "s_PurposeAr",
        "n_PurposeID",
        allVisitPurposes
      ),
    [allVisitPurposes]
  );

  const [isVehicleInfoVisible, setIsVehicleInfoVisible] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,
    setValue,
  } = useFormWithSchema(createVIPSchema(visitPurposes, user?.n_RoleID), {
    reValidateMode: "onChange",
    defaultValues: {
      hostEmail: user?.email,
      visitReqType: VisitorType.VVIP,
      visitStartDate:
        new Date() > new Date(new Date().setHours(7, 0, 0, 0))
          ? formattedDateTime(new Date())
          : formattedDateTime(new Date(new Date().setHours(7, 0, 0, 0))),
      visitEndDate: formattedDateTime(
        new Date(
          (new Date() > new Date(new Date().setHours(7, 0, 0, 0))
            ? new Date()
            : new Date(new Date().setHours(7, 0, 0, 0))
          ).getTime() +
            60 * 60 * 1000
        )
      ),
    },
    context: {
      isVehicleInfoVisible,
      selectedIdType: DOC_TYPE_ID.NATIONAL_ID,
    },
  });

  const [addedDevices, setAddedDevices] = useState<Device[]>();

  const [isLoading, setIsLoading] = useState(false);

  const { data: visitingHours } = useGetVisitingHours();

  const visitAllowedDuration = visitingHours?.visitorsMaxAllowedDays - 1;

  const { mutateAsync: ValidatePhoto, isPending: isValidatePhotoLoading } =
    useValidatePhoto();

  const { data: validatePhoto } = useGetValidatePhoto();

  const [isValidatePhoto, setIsValidatePhoto] = useState<boolean>(false);

  useEffect(() => {
    if (validatePhoto) {
      setIsValidatePhoto(validatePhoto.isValidatePhotoFromFR);
    }
  }, [validatePhoto]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      toast.error(t("fillAllInfo"));
    }
  }, [errors, t]);

  useEffect(() => {
    const fetchedAppointmentDetails = existingVisitorAppointment?.appointment;
    const fetchedVehicleDetails = existingVisitorAppointment?.vehicle;

    if (existingVisitorAppointment) {
      const userData = {
        id: fetchedAppointmentDetails?.n_AppointmentID,
        name:
          fetchedAppointmentDetails?.s_FullName ||
          fetchedAppointmentDetails?.s_VisitorName_En,
        companyName:
          fetchedAppointmentDetails?.s_CompanyName ||
          fetchedAppointmentDetails?.s_Sponsor,
        nationality: fetchedAppointmentDetails?.nationality,
        number: fetchedAppointmentDetails?.s_MobileNo,
        email:
          fetchedAppointmentDetails?.s_Email ||
          fetchedAppointmentDetails?.visitorEmail,

        dateOfBirth:
          fetchedAppointmentDetails?.dt_DateOfBirth &&
          formattedDate(new Date(fetchedAppointmentDetails?.dt_DateOfBirth)),

        idType: fetchedAppointmentDetails?.n_DocumentType,
        eid: fetchedAppointmentDetails?.eidNumber,
        eidExpiryDate:
          fetchedAppointmentDetails?.dt_EIDExpiryDate &&
          formattedDate(new Date(fetchedAppointmentDetails?.dt_EIDExpiryDate)),
        passportNumber: fetchedAppointmentDetails?.passportNumber,
        passportExpiryDate:
          fetchedAppointmentDetails?.dt_PassportExpiryDate &&
          formattedDate(
            new Date(fetchedAppointmentDetails?.dt_PassportExpiryDate)
          ),

        visa: fetchedAppointmentDetails?.s_VisaNo,
        visaExpiryDate:
          fetchedAppointmentDetails?.dt_VisaExpiryDate &&
          formattedDate(new Date(fetchedAppointmentDetails?.dt_VisaExpiryDate)),

        iqama: fetchedAppointmentDetails?.s_Iqama,
        iqamaExpiryDate: fetchedAppointmentDetails?.dt_IqamaExpiry,

        idTypeOtherName: fetchedAppointmentDetails?.s_OthersDoc,
        idTypeOtherNumber: fetchedAppointmentDetails?.s_OthersValue,
        idTypeOtherExpiryDate: fetchedAppointmentDetails?.dt_OthersExpiry,

        plateSource: fetchedVehicleDetails?.n_PlateSource,
        plateCode: fetchedVehicleDetails?.s_PlateCode,
        plateNumber: fetchedVehicleDetails?.s_PlateNumber,
        plateLetter1: fetchedVehicleDetails?.n_PlateLetter1,
        plateLetter2: fetchedVehicleDetails?.n_PlateLetter2,
        plateLetter3: fetchedVehicleDetails?.n_PlateLetter3,

        location: fetchedAppointmentDetails?.n_LocationID,
        building: fetchedAppointmentDetails?.n_BuildingID,
        gate: fetchedAppointmentDetails?.n_GateID,
        meetingRoom: fetchedAppointmentDetails?.n_MeetingRoomID,
        visitingDepartment: fetchedAppointmentDetails?.n_DepartmentID,
        visitReqType: fetchedAppointmentDetails?.n_VisitType,
        visitPurpose: fetchedAppointmentDetails?.purpose,
        visitPurposeOther: fetchedAppointmentDetails?.purposeOtherValue,
        hostEmail: fetchedAppointmentDetails?.s_VisitingPersonEmail,
        hostMobileNumber: fetchedAppointmentDetails?.s_HostMobileNo,
        visitStartDate: fetchedAppointmentDetails?.dt_AppointmentStartTime,
        visitEndDate: fetchedAppointmentDetails?.dt_AppointmentEndTime,
        vehicleNo: fetchedAppointmentDetails?.s_VehicleNo,
        note: fetchedAppointmentDetails?.s_Remarks,
        reqAccessLevels: fetchedAppointmentDetails?.s_HostAccessAreasIDs,
        accessLevels: fetchedAppointmentDetails?.s_MaxAccessLevelIDs,

        uploadPhotoType: fetchedAppointmentDetails?.s_PhotoContentType,
        uploadVehicleRegLicenseType:
          fetchedAppointmentDetails?.s_VehicleRegistrationContentType,
        uploadEidType: fetchedAppointmentDetails?.s_EIDContentType,
        uploadPassportType: fetchedAppointmentDetails?.s_PassportContentType,

        devices: fetchedAppointmentDetails?.devices,
        nextDevicesApproverRoleId:
          fetchedAppointmentDetails?.n_NextDevicesApproverRoleId,
        devicesApprovalStatus:
          fetchedAppointmentDetails?.n_DevicesApprovalStatus,
        approvalStatus: fetchedAppointmentDetails?.approvalStatus,
      };
      setUserAppointmentDetails(userData);

      const fetchedDevices = userData?.devices?.map((item: Device) => ({
        ...item,
        deviceTypeOther: item.deviceTypeOthersValue,
        devicePurposeOther: item.devicePurposeOthersValue,
      }));
      setAddedDevices(fetchedDevices);

      if (!fetchedVehicleDetails) {
        setIsVehicleInfoVisible(false);
      } else {
        setIsVehicleInfoVisible(true);
      }

      if (userData) {
        setValue("name", userData?.name || "");
        setValue("companyName", userData?.companyName || "");
        setValue("nationality", userData?.nationality || "");
        setValue("number", userData?.number || "");
        setValue("email", userData?.email || "");
        setValue(
          "dateOfBirth",
          userData?.dateOfBirth
            ? formattedDate(new Date(userData.dateOfBirth))
            : ""
        );
        setValue("idType", userData?.idType || "");

        userData?.iqama && setValue("iqama", userData?.iqama);
        new Date(userData?.iqamaExpiryDate) >= new Date() ||
        location?.pathname !== APP_ROUTES.VIP_VISIT_REQUEST
          ? setValue("iqamaExpiryDate", userData?.iqamaExpiryDate)
          : setValue("iqamaExpiryDate", "");

        userData?.eid && setValue("eid", userData?.eid);
        new Date(userData?.eidExpiryDate) >= new Date() ||
        location?.pathname !== APP_ROUTES.INDIVIDUAL_VISIT_REQUEST
          ? setValue("eidExpiryDate", userData?.eidExpiryDate)
          : setValue("eidExpiryDate", "");

        userData?.passportNumber &&
          setValue("passportNumber", userData?.passportNumber);
        new Date(userData?.passportExpiryDate) >= new Date() ||
        location?.pathname !== APP_ROUTES.INDIVIDUAL_VISIT_REQUEST
          ? setValue("passportExpiryDate", userData?.passportExpiryDate)
          : setValue("passportExpiryDate", "");

        userData?.visa && setValue("visaNumber", userData?.visa);
        new Date(userData?.visaExpiryDate) >= new Date() ||
        location?.pathname !== APP_ROUTES.INDIVIDUAL_VISIT_REQUEST
          ? setValue("visaExpiryDate", userData?.visaExpiryDate)
          : setValue("visaExpiryDate", "");

        setValue("idTypeOtherName", userData?.idTypeOtherName || "");
        userData?.idTypeOtherNumber &&
          setValue("idTypeOtherNumber", userData?.idTypeOtherNumber);
        new Date(userData?.idTypeOtherExpiryDate) >= new Date() ||
        location?.pathname !== APP_ROUTES.INDIVIDUAL_VISIT_REQUEST
          ? setValue("idTypeOtherExpiryDate", userData?.idTypeOtherExpiryDate)
          : setValue("idTypeOtherExpiryDate", "");

        setValue("vehicleNo", userData?.vehicleNo || "");

        setValue("plateSource", userData?.plateSource || "");
        setValue("plateNumber", userData?.plateNumber || null);
        setValue("plateLetter1", userData?.plateLetter1 || "");
        setValue("plateLetter2", userData?.plateLetter2 || "");
        setValue("plateLetter3", userData?.plateLetter3 || "");
      }
    }
  }, [existingVisitorAppointment, location?.pathname, setValue]);

  const selectedIdType = watch("idType");
  const visitReqType = watch("visitReqType");

  const uploadPhotoRef = useRef<HTMLInputElement | null>(null);
  const uploadEidRef = useRef<HTMLInputElement | null>(null);
  const uploadPassportRef = useRef<HTMLInputElement | null>(null);
  const uploadVehicleRegLicenseRef = useRef<HTMLInputElement | null>(null);
  const uploadHostEmployeeCardRef = useRef<HTMLInputElement | null>(null);
  const uploadIqamaRef = useRef<HTMLInputElement | null>(null);
  const uploadIdOtherRef = useRef<HTMLInputElement | null>(null);
  const uploadVisaRef = useRef<HTMLInputElement | null>(null);

  const [photoFileName, setPhotoFileName] = useState("");
  const [photoFileType, setPhotoFileType] = useState("");

  const [eidFileName, setEidFileName] = useState("");
  const [eidFileType, setEidFileType] = useState("");

  const [passportFileName, setPassportFileName] = useState("");
  const [passportFileType, setPassportFileType] = useState("");

  const [vehicleRegLicenseFileName, setVehicleRegLicenseFileName] =
    useState("");
  const [vehicleRegLicenseFileType, setVehicleRegLicenseFileType] =
    useState("");

  const [hostEmployeeCardFileName, setHostEmployeeCardFileName] = useState("");
  const [hostEmployeeCardFileType, setHostEmployeeCardFileType] = useState("");

  const [visaFileName, setVisaFileName] = useState("");
  const [visaFileType, setVisaFileType] = useState("");

  const [iqamaFileName, setIqamaFileName] = useState("");
  const [iqamaFileType, setIqamaFileType] = useState("");

  const [idOtherFileName, setIdOtherFileName] = useState("");
  const [idOtherFileType, setIdOtherFileType] = useState("");

  const selectedVisitPurpose = watch("visitPurpose");
  const visitStartDate = new Date(watch("visitStartDate") || "");

  // const isMultiDayVisit =
  //   visitStartDate instanceof Date &&
  //   visitEndDate instanceof Date &&
  //   !isNaN(visitStartDate.getTime()) &&
  //   !isNaN(visitEndDate.getTime()) &&
  //   Math.floor(
  //     (visitEndDate.getTime() - visitStartDate.getTime()) /
  //       (1000 * 60 * 60 * 24)
  //   ) >= 1;

  const {
    mutateAsync: AddVisitorAppointment,
    isPending: isAddVisitorAppointmentLoading,
  } = useAddVisitorAppointment();
  const { mutateAsync: AddAttachment, isPending: isAddAttachmentLoading } =
    useAddAttachment();

  const isVisitPurposeOther = useMemo(() => {
    const purpose =
      visitPurposes?.find((item) => item.value === Number(selectedVisitPurpose))
        ?.labelEn || "";

    return purpose.trim() === "Other";
  }, [visitPurposes, selectedVisitPurpose]);

  const { mutateAsync: ApproveDevices, isPending: isApproveDevicesLoading } =
    useApproveDevice();

  const { data: dynamicWorkflow } = useGetDynamicWorkflowsByIdByNonAdmin({
    encryptedId: DYNAMIC_WORKFLOW.DEVICES_APPROVAL_ONE_DAY_VISIT,
  });

  const { mutateAsync: GetDeviceDetailsById } = useGetDeviceDetailsById();

  const handleDevicesApproval = useCallback(
    async (
      appointmentId: string,
      devices: Device[],
      visitStartDate: string,
      visitEndDate: string
    ) => {
      if (appointmentId) {
        try {
          const body = {
            EncryptedId: appointmentId.toString(),
            n_RoleID: user?.n_RoleID,
            approvalDevices: devices?.map((item: Device) => ({
              appointmentDeviceId: item.appointmentDeviceId,
              N_ApprovalStatus: item.approvalStatus || 0,
              N_CurrentApprovalStatus: item.currentUserDeviceStatus || 0,
            })),
            Dt_AppointmentStartTime: visitStartDate,
            Dt_AppointmentEndTime: visitEndDate,
          };

          await ApproveDevices(body);
        } catch (error) {
          toast.error(t("somethingWentWrong"));
          return;
        }
      }
    },
    [user?.n_RoleID, ApproveDevices, t]
  );

  const onSubmit = useCallback(
    async (data: VIPData) => {
      const isAppointmentWithinVisitingHours = isVisitWithinVisitingHours(
        data.visitStartDate,
        data.visitEndDate,
        visitingHours
      );

      if (new Date(data.visitEndDate) <= new Date(data.visitStartDate)) {
        toast.error(t("visitDateError"));
      } else if (!isAppointmentWithinVisitingHours) {
        toast.error(
          t("vistingHoursAreFrom") +
            visitingHours.visitorsStartTime +
            t("to") +
            visitingHours.visitorsEndTime +
            "."
        );
      } else if (
        data.iqama &&
        data.idType === DOC_TYPE_ID.IQAMA &&
        onlyDate(data.iqama) < onlyDate(data.visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        data.eidExpiryDate &&
        data.idType === DOC_TYPE_ID.NATIONAL_ID &&
        onlyDate(data.eidExpiryDate) < onlyDate(data.visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        data.passportExpiryDate &&
        data.idType === DOC_TYPE_ID.PASSPORT &&
        onlyDate(data.passportExpiryDate) < onlyDate(data.visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        data.idTypeOtherExpiryDate &&
        data.idType === DOC_TYPE_ID.OTHER &&
        onlyDate(data.idTypeOtherExpiryDate) < onlyDate(data.visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        data.visaExpiryDate &&
        data.idType === DOC_TYPE_ID.VISA &&
        onlyDate(data.visaExpiryDate) < onlyDate(data.visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        isDeviceDetails &&
        (!addedDevices || addedDevices?.length === 0)
      ) {
        toast.error(t("kindlyAddADevice"));
      } else {
        let isPhotoValid = false;

        const base64String =
          data.uploadPhoto && (await fileToBase64(data.uploadPhoto));

        // Validate Photo from FR only if isValidatePhoto is enabled + Photo is attached/uploaded (For VIPs Photo is Optional so incase of No Photo bypass the Validate Photo from FR even if its enabled)
        if (data.uploadPhoto && isValidatePhoto && base64String) {
          try {
            const body = {
              fullName: data.name,
              photo: base64String,
            };

            const res = await ValidatePhoto(body);
            isPhotoValid = res.isPhotoValid;
          } catch (error: any) {
            const { result } = error;
            const errorCode = result.photoErrors[0]?.errorCode;

            const errorMessages: Record<string, string> = {
              [INVALID_PHOTO_ERRORS.PHOTO_VALIDATION_FAILED]: t(
                "photoValidationFailed"
              ),
              [INVALID_PHOTO_ERRORS.PICTURE_FORMAT_ERROR]: t(
                // "The uploaded file format is not supported."
                "errorValidatingPhoto"
              ),
              [INVALID_PHOTO_ERRORS.WRONG_SIZE]: t("photoSizeIncorrect"),
              [INVALID_PHOTO_ERRORS.PICTURE_SIZE_TOO_SMALL]: t("photoTooSmall"),
              [INVALID_PHOTO_ERRORS.RESOLUTION_NO_SUPPORT]: t(
                "photoResolutionNotSupported"
              ),
              [INVALID_PHOTO_ERRORS.FACE_QUALITY_NOT_OK]: t("faceQualityNotOk"),
              [INVALID_PHOTO_ERRORS.NO_PICTURE]: t("noPhotoUploaded"),
              [INVALID_PHOTO_ERRORS.NO_FACE]: t("noFaceDetectedPhoto"),
              [INVALID_PHOTO_ERRORS.MULTIPLE_FACES_DETECTED]: t(
                "multipleFacesDetected"
              ),
              [INVALID_PHOTO_ERRORS.FACE_TOO_SMALL]: t("faceTooSmallPhoto"),
              [INVALID_PHOTO_ERRORS.PICTURE_TOO_BRIGHT]: t("photoTooBright"),
              [INVALID_PHOTO_ERRORS.PICTURE_TOO_DARK]: t("photoTooDark"),
              [INVALID_PHOTO_ERRORS.FACE_TOO_BLUR]: t("faceTooBlurry"),
              [INVALID_PHOTO_ERRORS.WRONG_FACE_POSE]: t(
                "faceNotProperlyAligned"
              ),
              [INVALID_PHOTO_ERRORS.DUPLICATE_FACE_IMAGE]:
                t("photoAlreadyUsed"),
              [INVALID_PHOTO_ERRORS.DUPLICATE_FACE_NUMBER]: t(
                "photoFaceAlreadyExists"
              ),
              [INVALID_PHOTO_ERRORS.DUPLICATE_FACE_NAME]: t(
                "faceAlreadyRegisteredSameName"
              ),
            };

            toast.error(errorMessages[errorCode] ?? t("somethingWentWrong"));
            return;
          }
        } else {
          // For VIPs Photo is Optional so incase of No Photo bypass the Validate Photo from FR
          isPhotoValid = true;
        }

        if (data.uploadPhoto && isValidatePhoto && !isPhotoValid) {
          return;
        }

          try {
            const body = {
              N_VisitCreatedFrom: VISIT_CREATED_FROM.VMS,
              N_VisitUpdatedFrom: VISIT_CREATED_FROM.VMS,
              N_RoleID: user?.n_RoleID,
              FullName: data.name,
              Sponsor: data.companyName,
              MobileNo: data.number,
              Email: data.email,
              dateOfBirth: data.dateOfBirth
                ? formattedDate(new Date(data.dateOfBirth), true)
                : null,
              N_VisitType: Number(VisitorType.VVIP),
              IDType: data.idType || null,
              S_Iqama:
                data.idType === DOC_TYPE_ID.IQAMA && data.iqama
                  ? data.iqama
                  : "",
              EIDNumber:
                data.idType === DOC_TYPE_ID.NATIONAL_ID && data.eid
                  ? data.eid
                  : "",
              PassportNumber:
                data.idType === DOC_TYPE_ID.PASSPORT && data.passportNumber
                  ? data.passportNumber
                  : "",
              S_OthersDoc:
                data.idType === DOC_TYPE_ID.OTHER ? data.idTypeOtherName : "",
              S_OthersValue:
                data.idType === DOC_TYPE_ID.OTHER && data.idTypeOtherNumber
                  ? data.idTypeOtherNumber
                  : "",

              S_VisaNo:
                data.idType === DOC_TYPE_ID.VISA && data.visaNumber
                  ? data.visaNumber
                  : "",

              Dt_IqamaExpiry:
                data.iqamaExpiryDate && data.idType === DOC_TYPE_ID.IQAMA
                  ? formattedDate(new Date(data.iqamaExpiryDate), true)
                  : null,
              Dt_EIDExpiryDate:
                data.eidExpiryDate && data.idType === DOC_TYPE_ID.NATIONAL_ID
                  ? formattedDate(new Date(data.eidExpiryDate), true)
                  : null,
              Dt_PassportExpiryDate:
                data.passportExpiryDate && data.idType === DOC_TYPE_ID.PASSPORT
                  ? formattedDate(new Date(data.passportExpiryDate), true)
                  : null,
              Dt_OthersExpiry:
                data.idTypeOtherExpiryDate && data.idType === DOC_TYPE_ID.OTHER
                  ? formattedDate(new Date(data.idTypeOtherExpiryDate), true)
                  : null,
              Dt_VisaExpiryDate:
                data.visaExpiryDate && data.idType === DOC_TYPE_ID.VISA
                  ? formattedDate(new Date(data.visaExpiryDate), true)
                  : null,

              s_VehicleNo: data.vehicleNo,

              Nationality: data.nationality,
              N_LocationID: Number(data.location),
              N_BuildingID: Number(data.building),
              N_DepartmentID: Number(data.visitingDepartment),
              n_GateID: data.gate ? Number(data.gate) : 0,
              n_MeetingRoomID: Number(data.meetingRoom),
              S_VisitingPersonEmail: data.hostEmail,
              S_HostMobileNo: data.hostMobileNumber,
              Dt_AppointmentStartTime: formattedDateTime(
                new Date(data.visitStartDate)
              ),
              Dt_AppointmentEndTime: formattedDateTime(
                new Date(data.visitEndDate)
              ),

              Devices: isDeviceDetails
                ? addedDevices?.map((item: any) => ({
                    appointmentDeviceId: item.appointmentDeviceId || 0,
                    DeviceType: item.deviceType,
                    DeviceTypeOthersValue: item.deviceTypeOther,
                    DeviceModel: item.deviceModel,
                    SerialNumber: item.serialNumber,
                    // DevicePurpose: item.devicePurpose,
                    Purpose: item.purpose,
                    DevicePurposeOthersValue: item.devicePurposeOther,
                    ApprovalStatus: item.approvalStatus || STATUS_IDS.PENDING,
                  }))
                : null,

              N_PlateSource: isVehicleInfoVisible
                ? Number(data.plateSource)
                : 0,
              S_PlateCode: isVehicleInfoVisible ? data.plateCode : "",
              S_PlateNumber: isVehicleInfoVisible ? data.plateNumber : 0,
              N_PlateLetter1: isVehicleInfoVisible
                ? Number(data.plateLetter1)
                : 0,
              N_PlateLetter2: isVehicleInfoVisible
                ? Number(data.plateLetter2)
                : 0,
              N_PlateLetter3: isVehicleInfoVisible
                ? Number(data.plateLetter3)
                : 0,

              ApprovalStatus: STATUS_IDS.PENDING,
              Purpose: Number(data.visitPurpose),
              PurposeOtherValue: isVisitPurposeOther
                ? data.visitPurposeOther
                : "",
              Remarks: data.note,
              S_GroupKey: "",
              user_id: user?.user_id,
              N_IsExternalAppointment: false,
              N_CreatedBy_Staff: user?.user_id,
              N_UpdatedBy_Staff: user?.user_id,
              N_CreatedBy_External: 0,
              S_VehicleNo: data.vehicleNo,
              N_IsMobileAllowed: false,
              N_IsLaptopAllowed: false,
              S_AllowedReason: "",
              n_SelfPass: 3,
              S_HostAccessAreasIDs: data.reqAccessLevels,
              S_MaxAccessLevelIDs: data.accessLevels || null,
            };

            const res = await AddVisitorAppointment(body);

            if (res) {
              const appointmentID = res.id;

              const attachmentBodies: AddAttachments[] = [
                {
                  Id: appointmentID,
                  File: data.uploadPhoto,
                  S_FileType: photoFileType,
                  S_FileFieldName: "S_PhotoUpload",
                  S_FiledFileType: "S_PhotoContentType",
                },
                {
                  Id: appointmentID,
                  File:
                    data.idType === DOC_TYPE_ID.IQAMA ? data.uploadIqama : "",
                  S_FileType:
                    data.idType === DOC_TYPE_ID.IQAMA ? iqamaFileType : "",
                  S_FileFieldName: "S_IqamaUpload",
                  S_FiledFileType: "S_IqamaContentType",
                },
                {
                  Id: appointmentID,
                  File:
                    data.idType === DOC_TYPE_ID.NATIONAL_ID
                      ? data.uploadEid
                      : "",
                  S_FileType:
                    data.idType === DOC_TYPE_ID.NATIONAL_ID ? eidFileType : "",
                  S_FileFieldName: "S_EIDFile",
                  S_FiledFileType: "S_EIDContentType",
                },
                {
                  Id: appointmentID,
                  File:
                    data.idType === DOC_TYPE_ID.PASSPORT
                      ? data.uploadPassport
                      : "",
                  S_FileType:
                    data.idType === DOC_TYPE_ID.PASSPORT
                      ? passportFileType
                      : "",
                  S_FileFieldName: "S_PassportFile",
                  S_FiledFileType: "S_PassportContentType",
                },
                {
                  Id: appointmentID,
                  File:
                    data.idType === DOC_TYPE_ID.OTHER ? data.uploadIdOther : "",
                  S_FileType:
                    data.idType === DOC_TYPE_ID.OTHER ? idOtherFileType : "",
                  S_FileFieldName: "S_OthersUpload",
                  S_FiledFileType: "S_OthersContentType",
                },
                {
                  Id: appointmentID,
                  File: data.idType === DOC_TYPE_ID.VISA ? data.uploadVisa : "",
                  S_FileType:
                    data.idType === DOC_TYPE_ID.VISA ? visaFileType : "",
                  S_FileFieldName: "S_VisaFile",
                  S_FiledFileType: "S_VisaContentType",
                },
                {
                  Id: appointmentID,
                  File: data.uploadVehicleRegLicense,
                  S_FileType: vehicleRegLicenseFileType,
                  S_FileFieldName: "S_VehicleRegistrationFile",
                  S_FiledFileType: "S_VehicleRegistrationContentType",
                },
                {
                  Id: appointmentID,
                  File: data.uploadHostEmployeeCard,
                  S_FileType: hostEmployeeCardFileType,
                  S_FileFieldName: "S_HostEmployeeCardFile",
                  S_FiledFileType: "S_HostEmployeeCardContentType",
                },
              ];

              const formDatas = attachmentBodies.map((body) => {
                const formData = new FormData();
                formData.append("Id", body.Id.toString());
                formData.append("File", body.File ?? "");
                formData.append("S_FileType", body.S_FileType ?? "");
                formData.append("S_FileFieldName", body.S_FileFieldName);
                formData.append("S_FiledFileType", body.S_FiledFileType);
                return formData;
              });
              try {
                const uploadPromises = formDatas.map((body, idx) => {
                  if (attachmentBodies[idx].File) {
                    AddAttachment(body);
                  }
                });
                await Promise.all(uploadPromises);
              } catch (error) {
                toast.error(t("somethingWentWrongAttachments"));
              }

              if (
                isDeviceDetails &&
                dynamicWorkflow?.[0].n_RoleID === user?.n_RoleID
              ) {
                const body = {
                  encryptedId: appointmentID,
                };
                const devices = await GetDeviceDetailsById(body);

                handleDevicesApproval(
                  appointmentID,
                  devices,
                  formattedDateTime(new Date(data.visitStartDate)),
                  formattedDateTime(new Date(data.visitEndDate))
                );
              }
            }

            toast.success(t("appointmentCreated"));

            setTimeout(() => {
              navigate(APP_ROUTES.SEARCH_VISIT_REQUEST);
            }, 700);
          } catch (error: any) {
            const errorCode = error.errors && Object.keys(error.errors)[0];

            if (errorCode === API_ERRORS.MAX_ALLOWED_DAYS_EXCEEDED) {
              toast.error(t("maxAllowedDaysExceeded"));
            } else {
              toast.error(t("somethingWentWrong"));
            }
          }
      }
    },
    [
      visitingHours,
      isDeviceDetails,
      addedDevices,
      t,
      isValidatePhoto,
      ValidatePhoto,
      user?.n_RoleID,
      user?.user_id,
      isVehicleInfoVisible,
      isVisitPurposeOther,
      AddVisitorAppointment,
      photoFileType,
      iqamaFileType,
      eidFileType,
      passportFileType,
      idOtherFileType,
      visaFileType,
      vehicleRegLicenseFileType,
      hostEmployeeCardFileType,
      dynamicWorkflow,
      AddAttachment,
      GetDeviceDetailsById,
      handleDevicesApproval,
      navigate,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-1 justify-between">
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("createVisitRequest")}
            <span className="text-sm">({t("vipVvip")})</span>
          </h1>

          <div className="flex items-center gap-2">
            {location.pathname === APP_ROUTES.APPROVE_REQUEST ? (
              <Button
                type="button"
                onClick={() => navigate(APP_ROUTES.APPROVE_VISIT_REQUEST)}
                className="max-w-max bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent dark:!ring-white dark:text-white"
              >
                {t("approveVisitRequests")}
              </Button>
            ) : user?.n_RoleID !== USER_ROLES_IDS.SECURITY ? (
              <Button
                type="button"
                onClick={() => navigate(APP_ROUTES.SEARCH_VISIT_REQUEST)}
                className="max-w-max bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent dark:!ring-white dark:text-white"
              >
                {t("searchVisitRequests")}
              </Button>
            ) : (
              <></>
            )}
            <BackButton />
          </div>
        </div>

        <SearchExistingVisitorInfoCard
          setExistingVisitorAppointment={setExistingVisitorAppointment}
          setIsLoading={setIsLoading}
        />

        {isLoading ? (
          <div className="flex justify-center">
            <Loader />
          </div>
        ) : (
          <>
            <VisitorDetailsCard
              selectedIdType={selectedIdType}
              control={control}
              errors={errors}
              isVIP={visitReqType == VisitorType.VVIP}
              userAppointmentDetails={userAppointmentDetails}
              isVehicleInfoVisible={isVehicleInfoVisible}
              setIsVehicleInfoVisible={setIsVehicleInfoVisible}
            />

            {isVehicleInfoVisible && (
              <VehicleInformationCard
                control={control}
                errors={errors}
                userAppointmentDetails={userAppointmentDetails}
                clearErrors={clearErrors}
                isVehicleInfoVisible={isVehicleInfoVisible}
              />
            )}

            <VisitDetailsCard
              control={control}
              errors={errors}
              isVIP={visitReqType == VisitorType.VVIP}
              userAppointmentDetails={userAppointmentDetails}
              clearErrors={clearErrors}
              visitStartDate={visitStartDate}
              setValue={setValue}
              visitPurposes={visitPurposes}
              isVisitPurposeOther={isVisitPurposeOther}
              isDeviceDetails={isDeviceDetails}
              setIsDeviceDetails={setIsDeviceDetails}
              cardNumber={isVehicleInfoVisible ? 3 : 2}
              visitAllowedDuration={visitAllowedDuration}
            />

            {isDeviceDetails && (
              <DeviceDetailsCard
                clearErrors={clearErrors}
                userAppointmentDetails={userAppointmentDetails}
                addedDevices={addedDevices}
                setAddedDevices={setAddedDevices}
                cardNumber={isVehicleInfoVisible ? 4 : 3}
              />
            )}

            {/* {user?.n_RoleID === USER_ROLES_IDS.SECURITY && (
              <AccessLevelsCard
                control={control}
                errors={errors}
                userAppointmentDetails={userAppointmentDetails}
                cardNumber={isDeviceDetails ? 4 : 3}
              />
            )} */}

            <AttachmentsCard
              control={control}
              errors={errors}
              selectedIdType={selectedIdType}
              uploadPhotoRef={uploadPhotoRef}
              photoFileName={photoFileName}
              setPhotoFileName={setPhotoFileName}
              setPhotoFileType={setPhotoFileType}
              uploadEidRef={uploadEidRef}
              eidFileName={eidFileName}
              setEidFileName={setEidFileName}
              setEidFileType={setEidFileType}
              uploadPassportRef={uploadPassportRef}
              passportFileName={passportFileName}
              setPassportFileName={setPassportFileName}
              setPassportFileType={setPassportFileType}
              uploadVisaRef={uploadVisaRef}
              visaFileName={visaFileName}
              setVisaFileName={setVisaFileName}
              setVisaFileType={setVisaFileType}
              uploadIqamaRef={uploadIqamaRef}
              iqamaFileName={iqamaFileName}
              setIqamaFileName={setIqamaFileName}
              setIqamaFileType={setIqamaFileType}
              uploadIdOtherRef={uploadIdOtherRef}
              idOtherFileName={idOtherFileName}
              setIdOtherFileName={setIdOtherFileName}
              setIdOtherFileType={setIdOtherFileType}
              isVIP={visitReqType == VisitorType.VVIP}
              userAppointmentDetails={userAppointmentDetails}
              uploadVehicleRegLicenseRef={uploadVehicleRegLicenseRef}
              setVehicleRegLicenseFileName={setVehicleRegLicenseFileName}
              setVehicleRegLicenseFileType={setVehicleRegLicenseFileType}
              vehicleRegLicenseFileName={vehicleRegLicenseFileName}
              uploadHostEmployeeCardRef={uploadHostEmployeeCardRef}
              hostEmployeeCardFileName={hostEmployeeCardFileName}
              setHostEmployeeCardFileName={setHostEmployeeCardFileName}
              setHostEmployeeCardFileType={setHostEmployeeCardFileType}
              cardNumber={
                isDeviceDetails && isVehicleInfoVisible
                  ? 5
                  : !isDeviceDetails && !isVehicleInfoVisible
                  ? 3
                  : 4
              }
            />

            <div className="flex flex-col gap-3 bg-white p-4 rounded-xl shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
              <CheckboxInput
                id="host-accompany-pledge"
                label={t("hostAccompanyPledge")}
                labelStyles="leading-[1.2]"
                checked={isHostAccompanyPledge}
                setChecked={setIsHostAccompanyPledge}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                isLoading={
                  isAddVisitorAppointmentLoading ||
                  isValidatePhotoLoading ||
                  isAddAttachmentLoading ||
                  isApproveDevicesLoading
                }
                disabled={
                  !isHostAccompanyPledge ||
                  isAddVisitorAppointmentLoading ||
                  isValidatePhotoLoading ||
                  isAddAttachmentLoading ||
                  isApproveDevicesLoading
                }
                className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                onClick={handleSubmit(onSubmit)}
              >
                {t("submit")}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default VIP;
